import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";

/**
 * Props for the ProtectedRoute component.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route protection component that requires authentication.
 * 
 * Features:
 * - Checks if user is authenticated before rendering children
 * - Subscribes to authentication state changes
 * - Shows loading state while checking authentication
 * - Redirects to /auth if user is not authenticated
 * 
 * This component wraps routes that require user authentication.
 * It ensures only logged-in users can access protected pages.
 */
export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Effect: Sets up authentication state monitoring.
   * 
   * Process:
   * 1. Subscribes to auth state changes (sign in, sign out, token refresh)
   * 2. Checks for existing session immediately
   * 3. Updates user state when auth state changes
   * 
   * The listener is set up first to catch any state changes that might occur
   * during the getSession() call.
   */
  useEffect(() => {
    let isMounted = true;
    console.log('[ProtectedRoute] Setting up auth state monitoring');

    // Set up auth state listener FIRST to catch any changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[ProtectedRoute] Auth state changed:', event, 'User:', session?.user?.id || 'null');
        if (isMounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false); // CRITICAL: Set loading to false when auth state changes
        }
      }
    );

    // Check for existing session by reading localStorage directly (bypasses stuck Supabase client)
    console.log('[ProtectedRoute] Checking for existing session in localStorage...');
    
    const checkSession = async () => {
      try {
        // First, try to read session directly from localStorage
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const sessionKey = SUPABASE_URL ? `sb-${SUPABASE_URL.split('//')[1].split('.')[0]}-auth-token` : null;
        const storedSession = sessionKey ? localStorage.getItem(sessionKey) : null;
        
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            if (sessionData.access_token && sessionData.user) {
              console.log('[ProtectedRoute] Found session in localStorage, setting user directly');
              if (isMounted) {
                setUser(sessionData.user);
                // Create a minimal session object
                setSession({
                  access_token: sessionData.access_token,
                  refresh_token: sessionData.refresh_token,
                  expires_at: sessionData.expires_at,
                  expires_in: sessionData.expires_in,
                  token_type: sessionData.token_type || 'bearer',
                  user: sessionData.user,
                } as Session);
                setLoading(false);
              }
              // Try to set session in Supabase client (non-blocking)
              supabase.auth.setSession({
                access_token: sessionData.access_token,
                refresh_token: sessionData.refresh_token,
              }).catch((err) => {
                console.warn('[ProtectedRoute] setSession failed (non-critical):', err);
              });
              return;
            }
          } catch (e) {
            console.warn('[ProtectedRoute] Error parsing stored session:', e);
          }
        }
        
        // Fallback: Try getSession with timeout (in case localStorage doesn't have it)
        console.log('[ProtectedRoute] No session in localStorage, trying getSession()...');
        const timeoutId = setTimeout(() => {
          if (isMounted) {
            console.warn('[ProtectedRoute] getSession() timeout after 2 seconds, continuing without session');
            setLoading(false);
          }
        }, 2000);
        
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<{ data: { session: null }; error: null }>((resolve) => {
          setTimeout(() => {
            resolve({ data: { session: null }, error: null });
          }, 2000);
        });

        const result = await Promise.race([sessionPromise, timeoutPromise]);
        clearTimeout(timeoutId);
        
        console.log('[ProtectedRoute] getSession result - Session:', result.data?.session ? 'exists' : 'null', 'Error:', result.error?.message || 'none');
        if (!isMounted) {
          console.log('[ProtectedRoute] Component unmounted, ignoring getSession result');
          return;
        }
        
        if (result.error) {
          console.error('[ProtectedRoute] Error getting session:', result.error);
          setSession(null);
          setUser(null);
        } else {
          setSession(result.data?.session ?? null);
          setUser(result.data?.session?.user ?? null);
        }
        setLoading(false);
      } catch (error) {
        console.error('[ProtectedRoute] Unexpected error getting session:', error);
        if (isMounted) {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      }
    };
    
    checkSession();

    return () => {
      console.log('[ProtectedRoute] Cleaning up auth state monitoring');
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  console.log('[ProtectedRoute] Render - Loading:', loading, 'User:', user?.id || 'null');

  if (loading) {
    console.log('[ProtectedRoute] Rendering loading state');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    console.log('[ProtectedRoute] No user, redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  console.log('[ProtectedRoute] User authenticated, rendering children');
  return <>{children}</>;
};
